import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * State that remembers itself in this browser — once someone has set it. Until
 * then the default is used and nothing is stored, so changing a default later
 * reaches everyone who never made a choice of their own.
 *
 * Every access is guarded: Safari in private mode throws on localStorage, and a
 * value written by an older build can be unparseable — neither should take the
 * app down over a UI preference.
 */
export function useLocalStorage(key, initial, isValid = () => true) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw === null) return initial
      const parsed = JSON.parse(raw)
      return isValid(parsed) ? parsed : initial
    } catch {
      return initial
    }
  })
  const chosen = useRef(false)

  useEffect(() => {
    if (!chosen.current) return
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      /* storage full / disabled — the preference just won't persist */
    }
  }, [key, value])

  const set = useCallback((next) => {
    chosen.current = true
    setValue(next)
  }, [])

  return [value, set]
}
